import { create } from 'zustand';

interface Modal {
  id: string;
  component: React.ComponentType<any>;
  props?: any;
}

interface UIState {
  // Sidebar
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  
  // Modals
  modals: Modal[];
  
  // Global loading
  isGlobalLoading: boolean;
  globalLoadingMessage?: string;
  
  // Notifications
  notifications: Notification[];
  
  // Theme
  theme: 'light' | 'dark' | 'system';
  
  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  
  openModal: (modal: Modal) => void;
  closeModal: (modalId: string) => void;
  closeAllModals: () => void;
  
  setGlobalLoading: (loading: boolean, message?: string) => void;
  
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (notificationId: string) => void;
  
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  timestamp: number;
}

const useUIStore = create<UIState>((set, get) => ({
  // Initial state
  isSidebarOpen: true,
  isSidebarCollapsed: false,
  modals: [],
  isGlobalLoading: false,
  globalLoadingMessage: undefined,
  notifications: [],
  theme: 'system',

  // Sidebar actions
  toggleSidebar: () => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
  },

  setSidebarOpen: (open: boolean) => {
    set({ isSidebarOpen: open });
  },

  toggleSidebarCollapsed: () => {
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }));
  },

  // Modal actions
  openModal: (modal: Modal) => {
    set((state) => ({
      modals: [...state.modals, modal],
    }));
  },

  closeModal: (modalId: string) => {
    set((state) => ({
      modals: state.modals.filter((modal) => modal.id !== modalId),
    }));
  },

  closeAllModals: () => {
    set({ modals: [] });
  },

  // Loading actions
  setGlobalLoading: (loading: boolean, message?: string) => {
    set({
      isGlobalLoading: loading,
      globalLoadingMessage: message,
    });
  },

  // Notification actions
  addNotification: (notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: Date.now(),
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove notification after duration
    if (notification.duration !== 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, notification.duration || 5000);
    }
  },

  removeNotification: (notificationId: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== notificationId),
    }));
  },

  // Theme actions
  setTheme: (theme: 'light' | 'dark' | 'system') => {
    set({ theme });
    
    // Apply theme to document
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    } else {
      document.documentElement.classList.toggle('dark', theme === 'dark');
    }
  },
}));

// Helper functions for common UI operations
export const showSuccess = (title: string, message?: string) => {
  useUIStore.getState().addNotification({
    type: 'success',
    title,
    message,
    timestamp: Date.now(),
  });
};

export const showError = (title: string, message?: string) => {
  useUIStore.getState().addNotification({
    type: 'error',
    title,
    message,
    duration: 10000, // Errors stay longer
    timestamp: Date.now(),
  });
};

export const showWarning = (title: string, message?: string) => {
  useUIStore.getState().addNotification({
    type: 'warning',
    title,
    message,
    timestamp: Date.now(),
  });
};

export const showInfo = (title: string, message?: string) => {
  useUIStore.getState().addNotification({
    type: 'info',
    title,
    message,
    timestamp: Date.now(),
  });
};

export default useUIStore;